import { useSnackbar } from 'notistack';
import { useState, useEffect, Fragment } from 'react';
// @mui
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  Typography,
  Box,
  TextField,
  IconButton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
// components
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
// utils
import axios from '../../utils/axios';

// ----------------------------------------------------------------------

const CAREER_OPTIONS = ["찾는 포지션과 차이가 있어요.", "연차가 너무 높아요.", "연차가 너무 낮아요.", "우리 팀과 핏이 안 맞을 것 같아요."];
const TERM_OPTIONS = [
  {
    label: "인건비가 너무 비싸요.",
    value: "인건비",
  },
  {
    label: "주당 업무 가능 시간이 너무 적어요.",
    value: "주당 업무 가능한 시간",
  },
];

const REJECT_TYPE_OPTIONS = (smUp) => [
  { label:
    <Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <SvgIconStyle src="/icons/icon-shield-user-mono.svg" sx={{ width: smUp ? 20 : 16, height: smUp ? 20 : 16 }} />
        <Typography variant={smUp ? "subtitle1" : "overline"}>
          크루의 경험/이력이 마음에 들지 않아요.
        </Typography>
      </Stack>
      <Typography variant="caption">커리어, 도메인 경험, 연차, 포지션, 핏 등</Typography>
    </Stack>,
    value: "career"
  },
  { label: 
    <Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        <SvgIconStyle src="/icons/icon-clock-mono.svg" sx={{ width: smUp ? 20 : 16, height: smUp ? 20 : 16 }} />
        <Typography variant={smUp ? "subtitle1" : "overline"}>
          크루가 제안한 조건이 마음에 들지 않아요.
        </Typography>
      </Stack>
      <Typography variant="caption">인건비, 주당 업무 가능 시간, 업무 시작 일자 등</Typography>
    </Stack>,
    value: "term" },
];

export default function RejectOrModifyCrewDialog({ isOpen, closeDialog, jobPost, candidate, passOffer, stopJobPost, modifyOffer }) {
  const smUp = useResponsive('up', 'sm');
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [isSubmit, setIsSubmit] = useState(false);
  // type
  const [rejectType, setRejectType] = useState(null);
  // reject
  const [rejectItems, setRejectItems] = useState([]);
  const [rejectMessage, setRejectMessage] = useState("");
  // modify
  const [modifyItems, setModifyItems] = useState([]);
  const [modifyMessage, setModifyMessage] = useState("");

  const { enqueueSnackbar } = useSnackbar();

  const hasSelected = (target, option) => {
    return target.includes(option);
  };

  const selectTermOption = (option) => {
    if (hasSelected(modifyItems, option)) {
      setModifyItems((prev) => {
        return prev.filter((item) => item !== option);
      });
    } else {
      setModifyItems((prev) => {
        return [...prev, option];
      });
    }
  };

  const selectCareerOption = (option) => {
    if (hasSelected(rejectItems, option)) {
      setRejectItems((prev) => {
        return prev.filter((item) => item !== option);
      });
    } else {
      setRejectItems((prev) => {
        return [...prev, option];
      });
    }
  };

  const resetAllState = async () => {
    setStep(0);
    setIsSubmit(false);
    setRejectType(null);
    setModifyMessage("");
    setModifyItems([]);
    setRejectMessage("");
    setRejectItems([]);
  }

  const goNextForm = async () => {
    setStep(step+1);
  };

  const goCareerOrTerm = async () => {
    if (rejectType === "career") {
      setStep(2);
    } else if (rejectType === "term") {
      setStep(3);
    }
  };

  const rejectMatching = async () => {
    try {
      setIsSubmit(true);
      const companyId = user?.employee.company[0].id;
      const jobPostId = jobPost?.id;
      const candidateId = candidate?.id;
      const rejectData = {
        reject_reason: [...rejectItems].filter(m => m).join('\n'),
        matching_feedback: rejectMessage || '-',
      };
      const response = await axios.post(
        `/api/company/${companyId}/job-posts/${jobPostId}/candidate/${candidateId}/pass/`,
        rejectData
      );
      setIsSubmit(false);
      enqueueSnackbar('다른 크루 매칭을 요청하였습니다.', { variant: 'success' });
      passOffer(response);
    } catch (err) {
      setIsSubmit(false);
      console.error(err);
    }
  };

  const requestModify = async () => {
    try {
      setIsSubmit(true);
      const companyId = user?.employee.company[0].id;
      const jobPostId = jobPost?.id;
      const candidateId = candidate?.id;
      await axios.post(`/api/company/${companyId}/job-posts/${jobPostId}/candidate/${candidateId}/modify/`, {
        items: [...modifyItems].filter(m => m).join(', '),
        message: modifyMessage,
      });
      setIsSubmit(false);
      setStep(4);
    } catch (err) {
      console.error(err);
      setIsSubmit(false);
    }
  };

  const goMeetingGuide = async () => {
    setStep(5);
  };

  const rejectAndStop = async () => {
    setIsSubmit(true);
    stopJobPost({reason: "이미 채용을 완료했어요"})
  };

  useEffect(() => {
    if (isOpen) {
      resetAllState();
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} maxWidth="xs" fullWidth fullScreen={!smUp && (step === 2 || step === 3)}>
      {step === -1 && ( // [step -1. 채용완료, 중단]
        <Fragment>
          <DialogContent>
            <Iconify icon={"eva:alert-triangle-fill"} sx={{ width: 24, height: 24, color: 'error.main' }} />
            <Typography variant={smUp ? "h5" : "subtitle1"} gutterBottom>
              이미 채용을 완료하셨나요?
            </Typography>
            <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }}>
              다른 분으로 채용을 완료하셨다면, 이 공고의 매칭을 중단해드릴게요.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Stack direction={{ xs: "column-reverse", sm: "row" }} sx={{ width: 1 }} spacing={1} justifyContent="flex-end">
            <Button
              onClick={() => setStep(1)}
              size="large"
              sx={{ px: 4 }}
            >
              돌아가기
            </Button>
            <Button
              variant="contained"
              onClick={rejectAndStop}
              size="large"
              sx={{ px: 2 }}
            >
              이미 채용을 완료했어요
            </Button>
            </Stack>
          </DialogActions>
        </Fragment>
      )}
      {step === 0 && ( // [step 0]
        <Fragment>
          <DialogContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Iconify icon={"eva:alert-triangle-fill"} sx={{ width: 20, height: 20, color: 'error.main' }} />
              <Typography variant="subtitle2" sx={{ color: "error.main" }}>
                잠깐, 꼭 확인해주세요
              </Typography>
            </Stack>
            <Typography variant={smUp ? "h5" : "subtitle1"} gutterBottom>
              이 크루를 다시는 매칭받을 수 없어요.
            </Typography>
            <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }}>
              그래도 다른 매칭을 받길 원하시는지 신중하게 결정해주세요.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Stack direction={{ xs: "column-reverse", sm: "row" }} sx={{ width: 1 }} spacing={1} justifyContent="flex-end">
            <Button
              onClick={closeDialog}
              size="large"
              sx={{ px: 4 }}
            >
              돌아가기
            </Button>
            <Button
              variant="contained"
              onClick={goNextForm}
              size="large"
              sx={{ px: 4 }}
            >
              다른 크루 매칭받기
            </Button>
            </Stack>
          </DialogActions>
        </Fragment>
      )}
      {step === 2 && ( // [step 2]
        <Fragment>
          <DialogContent>
            <IconButton color="inherit" edge="start" sx={{ mt: 0.5 }} onClick={() => setStep(1)}>
              <SvgIconStyle src={`/icons/chevron-left.svg`} sx={{ width: 24, height: 24, color: "primary.main" }} />
            </IconButton>
            <Typography variant={smUp ? "h5" : "subtitle1"} gutterBottom>
              이번에 매칭된 크루의 어떤 부분이<br/>원하시는 스펙과 달랐나요?
            </Typography>
            <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }} paragraph>
              만족스러운 제안을 드리기 위해서 여쭙습니다.
            </Typography>
            <Stack spacing={1} sx={{ mb: 5 }}>
              {CAREER_OPTIONS.map((option, index) => {
                return (
                  <Stack direction="row" alignItems="center"
                    key={index}
                    sx={{
                      background: (theme) => hasSelected(rejectItems, option) && theme.palette.grey[200],
                      '&:hover': {
                        cursor: 'pointer',
                        background: (theme) => theme.palette.grey[200],
                      },
                      borderRadius: 1,
                      border: (theme) => hasSelected(rejectItems, option) ? '1px solid black' : `1px solid ${theme.palette.grey[500_32]}`,
                      py: 0.5,
                      px: 0.5,
                    }}
                    onClick={(e) => {e.stopPropagation(); selectCareerOption(option)}}
                  >
                    <Checkbox
                      checked={hasSelected(rejectItems, option)}
                      onClick={(e) => {e.stopPropagation(); selectCareerOption(option)}}
                    />
                    <Typography variant={smUp ? "body1" : "body2"} sx={{ wordBreak: 'break-all', fontWeight: hasSelected(rejectItems, option) ? "bold" : "normal" }}>
                      {option}
                    </Typography>
                  </Stack>
                )
              })}
            </Stack>
            <Typography variant="subtitle1" paragraph>
              추가로 말씀해주실 내용이 있다면 알려주세요. 
            </Typography>
            <TextField
              fullWidth
              value={rejectMessage}
              onChange={(e) => setRejectMessage(e.target.value)}
              multiline
              minRows={4}
              placeholder="예. 커머스 경력이 있는 분을 원합니다 / 와이어프레임 작업이 가능한 분을 원합니다"
            />
          </DialogContent>
          <DialogActions>
            <LoadingButton
              onClick={closeDialog}
              loading={isSubmit}
              sx={{ px: { xs: 4, sm: 6 } }}
              size="large"
            >
              취소
            </LoadingButton>
            <LoadingButton
              variant="contained"
              onClick={rejectMatching}
              disabled={!rejectItems.length}
              loading={isSubmit}
              sx={{ px: 4 }}
              size="large"
            >
              제출하기
            </LoadingButton>
          </DialogActions>
        </Fragment>
      )}
      {step === 3 && ( // [step 3]
        <Fragment>
          <DialogContent>
            <IconButton color="inherit" edge="start" sx={{ mt: 0.5 }} onClick={() => setStep(1)}>
              <SvgIconStyle src={`/icons/chevron-left.svg`} sx={{ width: 24, height: 24, color: "primary.main" }} />
            </IconButton>
            <Typography variant={smUp ? "h5" : "subtitle1"} gutterBottom>
              크루에게 계약 조건을 조정해달라고 요청해보세요.
            </Typography>
            <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all", mb: 4 }}>
              제안 받은 분의 프로필은 마음에 드셨나요?<br/>
              계약 조건을 조정해달라고 제안해볼 수 있습니다.
            </Typography>
            <Typography variant="subtitle1" paragraph>
              크루가 제안한 조건 중 어떤 부분이 아쉬우셨나요?
            </Typography>
            <Stack spacing={1} sx={{ mb: 5 }}>
              {TERM_OPTIONS.map((option, index) => {
                return (
                  <Stack direction="row" alignItems="center"
                    key={index}
                    sx={{
                      background: (theme) => hasSelected(modifyItems, option.value) && theme.palette.grey[200],
                      '&:hover': {
                        cursor: 'pointer',
                        background: (theme) => theme.palette.grey[200],
                      },
                      borderRadius: 1,
                      border: (theme) => hasSelected(modifyItems, option.value) ? '1px solid black' : `1px solid ${theme.palette.grey[500_32]}`,
                      py: 0.5,
                      px: 0.5,
                    }}
                    onClick={(e) => {e.stopPropagation(); selectTermOption(option.value)}}
                  >
                    <Checkbox
                      checked={hasSelected(modifyItems, option.value)}
                      onClick={(e) => {e.stopPropagation(); selectTermOption(option.value)}}
                    />
                    <Typography variant={smUp ? "body1" : "body2"} sx={{ wordBreak: 'break-all', fontWeight: hasSelected(modifyItems, option.value) ? "bold" : "normal" }}>
                      {option.label}
                    </Typography>
                  </Stack>
                )
              })}
            </Stack>
            <Typography variant="subtitle1" paragraph>
              크루에게 전달할 메시지를 작성해주세요.
            </Typography>
            <TextField
              fullWidth
              value={modifyMessage}
              onChange={(e) => setModifyMessage(e.target.value)}
              multiline
              minRows={4}
              placeholder={`예) 주당 업무 시간을 더 많이 책정해주시면 좋겠습니다.
첫번째 계약이니 금액을 250만원 이하로 책정해주시면 좋겠습니다.`}
            />
          </DialogContent>
          <DialogActions>
            <Stack width={1} spacing={2}>
              <LoadingButton
                variant="contained"
                onClick={requestModify}
                disabled={!modifyItems.length}
                loading={isSubmit}
                size="large"
              >
                조건 조정 요청하기
              </LoadingButton>
              <LoadingButton
                variant="outlined"
                onClick={goMeetingGuide}
                loading={isSubmit}
                size="large"
              >
                크루와 미팅에서 조건을 조정할게요
              </LoadingButton>
            </Stack>
          </DialogActions>
        </Fragment>
      )}
      {step === 4 && ( // [step 4]
        <Fragment>
          <DialogContent>
            <Iconify icon={"eva:info-fill"} sx={{ width: 24, height: 24, color: 'info.main' }} />
            <Typography variant={smUp ? "h5" : "subtitle1"} gutterBottom>
              크루에게 계약 조건을 조정해달라는 메시지를 보냈어요.
            </Typography>
            <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }}>
              크루가 답변을 보내면 알림을 보내드릴게요.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              fullWidth
              variant="contained"
              onClick={() => { modifyOffer(); closeDialog(); } }
              size="large"
            >
              확인했어요
            </Button>
          </DialogActions>
        </Fragment>
      )}
      {step === 5 && ( // [step 5]
        <Fragment>
          <DialogContent>
            <Iconify icon={"eva:info-fill"} sx={{ width: 24, height: 24, color: 'info.main' }} />
            <Typography variant={smUp ? "h5" : "subtitle1"} gutterBottom>
              계약 플랜을 선택하고 미팅을 예약해주세요.
            </Typography>
            <Typography variant={smUp ? "body1" : "body2" } sx={{ wordBreak: "keep-all" }}>
              크루와 미팅에서 계약 조건을 협의해서 변경할 수 있어요.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              fullWidth
              variant="contained"
              onClick={closeDialog}
              size="large"
            >
              플랜 선택하러 가기
            </Button>
          </DialogActions>
        </Fragment>
      )}
      {step === 1 && ( // [step 1]
        <Fragment>
          <DialogContent>
            <Typography variant="h5" gutterBottom>
              제안이 만족스럽지 않은 이유를 선택해주세요.
            </Typography>
            <Typography variant="body2" paragraph>
              더 만족스러운 제안을 드리기 위해서 여쭙습니다.
            </Typography>
            <RadioGroup name="rejectTypeRadio" value={rejectType} onChange={(e) => setRejectType(e.target.value)}>
              {REJECT_TYPE_OPTIONS(smUp).map((option, index) => {
                return (
                  <FormControlLabel
                    sx={{
                      "&:hover": {background: (theme) => theme.palette.grey[200]},
                      background: (theme) => rejectType === option.value && theme.palette.grey[200],
                      mx: 0,
                      py: 1.5,
                      px: 2,
                      my: 1,
                      border: (theme) => rejectType === null ? `1px solid ${theme.palette.grey[500_32]}` : rejectType === option.value ? '1px solid black' : `1px solid ${theme.palette.grey[500_32]}`,
                      borderRadius: 1,
                      alignItems: "center",
                      color: rejectType === null ? "text.primary" : rejectType === option.value ? "text.primary" : "text.disabled",
                    }}
                    key={index}
                    value={option.value}
                    control={<Radio sx={{ width: 20, height: 20, mr: 1.5 }} />}
                    label={option.label}
                  />
                )}
              )}
            </RadioGroup>

            <Box sx={{ mt: 1 }}/>

            <Button
              type="button"
              size={smUp ? "medium" : "small"}
              sx={{
                color: "text.secondary",
                fontWeight: 'bold',
              }}
              onClick={() => setStep(-1)}
              startIcon={<Iconify icon={'eva:alert-circle-fill'} />}
            >
              이미 채용을 완료하셨나요?
            </Button>

          </DialogContent>
          <DialogActions>
            <Button
              onClick={closeDialog}
              sx={{ px: 6 }}
              size="large"
            >
              닫기
            </Button>
            <Button
              variant="contained"
              onClick={goCareerOrTerm}
              disabled={!rejectType}
              sx={{ px: 6 }}
              size="large"
            >
              선택완료
            </Button>
          </DialogActions>
        </Fragment>
      )}
    </Dialog>
  )
};
